import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { Roles } from '@app/core/guard/Roles';
import { CompanyResponse } from '@interfaces/common/company';
import { CorporateCharterCompanyStatus } from '@interfaces/corporate-charter/responses/corporate-charter.response';
import { CompanyService } from '@services/http/company.service';
import { CorporateCharterHttpService } from '@services/http/corporate-charter/corporate-charter-http.service';
import { AppState } from '@src/app/state';
import { selectRoles } from '@src/app/state/auth/auth.selectors';
import { Observable, catchError, map, of, shareReplay, switchMap, take } from 'rxjs';

/** Uma empresa no seletor do caminho. `status` nulo = situação desconhecida (lista de reserva, sem selo). */
export interface CharterSwitcherItem {
  companyId: string;
  companyName: string;
  cnpj: string | null;
  status: CorporateCharterCompanyStatus | null;
  pendingReviewCount: number;
}

/** A empresa aberta na tela (para o seletor abrir marcado nela). */
export interface CharterSwitcherCurrent {
  companyId: string;
  companyName: string | null;
  cnpj: string | null;
}

export interface CharterSwitcherResult {
  items: CharterSwitcherItem[];
  total: number;
}

/**
 * Dados do seletor de empresa da tela Societário. Busca em `GET corporate-charter/companies` (com a situação do
 * contrato social de cada uma); se esse endpoint falhar, passa a usar, até o fim da tela, a lista de empresas que os
 * outros seletores da plataforma usam (`GET companys`, filtrada aqui por nome ou CNPJ, sem selo). Provido pelo
 * componente do seletor.
 */
@Injectable()
export class CharterCompanySearchService {
  private readonly charterHttp = inject(CorporateCharterHttpService);
  private readonly companies = inject(CompanyService);
  private readonly store = inject<Store<AppState>>(Store);

  readonly pageSize = 20;

  private useFallback = false;
  private fallbackList$: Observable<CharterSwitcherItem[]> | null = null;

  /**
   * Busca as empresas. Sem termo, garante a empresa aberta na lista: se ela não veio na primeira página, entra no topo
   * (com a situação, quando o endpoint do Societário responde), para o seletor abrir marcado nela.
   */
  search(
    term: string,
    current: CharterSwitcherCurrent | null = null
  ): Observable<CharterSwitcherResult> {
    const search = term.trim();
    return this.searchPage(search).pipe(
      switchMap(result => {
        if (search || !current || result.items.some(i => i.companyId === current.companyId)) {
          return of(result);
        }
        return this.findCurrent(current).pipe(
          map(item => ({ items: [item, ...result.items], total: result.total }))
        );
      })
    );
  }

  private searchPage(search: string): Observable<CharterSwitcherResult> {
    if (this.useFallback) {
      return this.searchFallback(search);
    }
    return this.charterHttp
      .listCompanies({ search: search || null, page: 1, pageSize: this.pageSize })
      .pipe(
        map(result => {
          if (!result) {
            throw new Error('Lista do Societário vazia');
          }
          return {
            items: (result.data ?? []).map(item => ({
              companyId: item.companyId,
              companyName: item.companyName,
              cnpj: item.cnpj,
              status: item.status,
              pendingReviewCount: item.pendingReviewCount ?? 0,
            })),
            total: result.total ?? result.data?.length ?? 0,
          };
        }),
        catchError(() => {
          this.useFallback = true;
          return this.searchFallback(search);
        })
      );
  }

  /**
   * Se o usuário tem para onde trocar de empresa (e, portanto, faz sentido a lista Societário e o "Limpar seleção").
   * TENANT e ADMIN sempre; papel COMPANY só quando o escopo dele tem mais de uma empresa (`companys/my-companies`).
   * COMPANY com uma empresa só vê o nome sem seletor, e a lista o leva para a própria empresa.
   */
  canSwitch(): Observable<boolean> {
    return this.store.select(selectRoles).pipe(
      take(1),
      switchMap(roles => {
        const current = roles ?? [];
        const isCompanyOnly =
          current.includes(Roles.COMPANY) &&
          !current.includes(Roles.TENANT) &&
          !current.includes(Roles.ADMIN);
        if (!isCompanyOnly) {
          return of(true);
        }
        return this.companies.getMyCompanies().pipe(
          map(ids => (ids?.length ?? 0) > 1),
          catchError(() => of(false))
        );
      })
    );
  }

  /** A empresa aberta: na lista de reserva pelo id; no Societário, buscando pelo CNPJ (ou nome) e casando o id. */
  private findCurrent(current: CharterSwitcherCurrent): Observable<CharterSwitcherItem> {
    const synthetic: CharterSwitcherItem = {
      companyId: current.companyId,
      companyName: current.companyName || 'Empresa',
      cnpj: current.cnpj,
      status: null,
      pendingReviewCount: 0,
    };
    const lookup = (current.cnpj ?? '').replace(/\D/g, '') || current.companyName || '';
    if (!lookup) {
      return of(synthetic);
    }
    return this.searchPage(lookup).pipe(
      map(result => result.items.find(i => i.companyId === current.companyId) ?? synthetic),
      catchError(() => of(synthetic))
    );
  }

  private searchFallback(search: string): Observable<CharterSwitcherResult> {
    this.fallbackList$ ??= this.companies.get({ page: 1, pageSize: 1000 }).pipe(
      map(result => (result?.data ?? []).map(company => this.fromCompany(company))),
      shareReplay({ bufferSize: 1, refCount: false })
    );
    return this.fallbackList$.pipe(
      map(list => {
        const matches = list.filter(item => this.matches(item, search));
        return { items: matches.slice(0, this.pageSize), total: matches.length };
      })
    );
  }

  private fromCompany(company: CompanyResponse): CharterSwitcherItem {
    return {
      companyId: company.id,
      companyName: company.displayName || company.corporateName,
      cnpj: company.cnpj || null,
      status: null,
      pendingReviewCount: 0,
    };
  }

  /** Nome sem acento e sem caixa, ou os dígitos do CNPJ (a partir de 2 dígitos digitados). */
  private matches(item: CharterSwitcherItem, search: string): boolean {
    if (!search) {
      return true;
    }
    const term = normalize(search);
    if (normalize(item.companyName).includes(term)) {
      return true;
    }
    const digits = search.replace(/\D/g, '');
    return digits.length >= 2 && (item.cnpj ?? '').replace(/\D/g, '').includes(digits);
  }
}

function normalize(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim();
}
